import React from 'react';
import {
  Zap, TrendingUp, Settings, Shield, Info
} from 'lucide-react';
import { LLMDecision } from '../../types/api';
import { Card, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';

interface DecisionCardProps {
  decision: LLMDecision;
  isLatest: boolean;
  children: React.ReactNode;
}

const DECISION_TYPE_CONFIG = {
  initial: {
    label: 'Initial Analysis',
    icon: Zap,
    bgClass: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
  },
  position_monitoring: {
    label: 'Position Monitoring',
    icon: TrendingUp,
    bgClass: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
  },
  auto_configuration: {
    label: 'Auto Configuration',
    icon: Settings,
    bgClass: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400',
  },
  risk_management: {
    label: 'Risk Management Agent',
    icon: Shield,
    bgClass: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400',
  },
  default: {
    label: 'Market Analysis',
    icon: Info,
    bgClass: 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400',
  },
};

const ACTION_BADGE_VARIANT = {
  open_position: 'success',
  close_position: 'destructive',
  hold: 'secondary',
  default: 'outline',
} as const;

export const DecisionCard: React.FC<DecisionCardProps> = ({
  decision,
  isLatest,
  children,
}) => {
  const config = DECISION_TYPE_CONFIG[decision.decision_type as keyof typeof DECISION_TYPE_CONFIG]
    || DECISION_TYPE_CONFIG.default;
  const Icon = config.icon;

  const actionVariant = ACTION_BADGE_VARIANT[decision.action_taken as keyof typeof ACTION_BADGE_VARIANT]
    || ACTION_BADGE_VARIANT.default;

  return (
    <Card className={isLatest ? "border-primary/20 bg-primary/5" : ""}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-full ${config.bgClass}`}>
              <Icon className="h-4 w-4" />
            </div>
            <div>
              <CardTitle className="text-base">{config.label}</CardTitle>
              <CardDescription>
                {new Date(decision.timestamp).toLocaleString()}
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {decision.action_taken && (
              <Badge variant={actionVariant}>
                {decision.action_taken.replace('_', ' ')}
              </Badge>
            )}
            {isLatest && <Badge variant="default">Latest</Badge>}
          </div>
        </div>
      </CardHeader>
      {children}
    </Card>
  );
};

export default DecisionCard;
